/* ═══════════════════════════════════════════════════════════════
   taskPermissions — ONE pure helper module for the Tasks Kit: see /
   edit / delete / archive / export / comment / assign, plus the
   underlying visibility rules. Folds in the former lib-level task
   visibility helpers (visibleMemberIds / isTaskVisible /
   taskBelongsToProject / canEditTasks — still used by the project Tasks +
   Attention widgets; taskBelongsToProject later dropped its task.projectId
   fallback per Decision 20 — task_project_links is the only project truth)
   alongside the new org-chart-based helper set the Tasks Kit rebuild
   uses for its own visibility filtering. No React, no services —
   inputs in, booleans/sets out.
   ═══════════════════════════════════════════════════════════════ */
import type {
  Task,
  TaskAssignment,
  TaskProjectLink,
  TeamMember,
  Team,
  TeamAssignment,
} from '../../../shared/types';
import { hasPermission, hasAnyPermission } from '../../../shared/permissions';
import {
  isBelowOnOrgChart,
  assignableMembers as orgAssignableMembers,
  type OrgChartMember,
} from '../../../shared/orgChart';

// ─── Legacy (former lib-level task visibility helpers) — moved unchanged.
// Still used by the project Tasks widget and Attention widget (T5 wires
// them to the new helpers below). ───

export interface VisibleMemberIdsInput {
  userId: string;
  permissions: string[] | undefined;
  members: TeamMember[];
  teams: Team[];
  teamAssignments: TeamAssignment[];
}

/**
 * Mirrors the former Tasks page's (pre-split) visibility derivation:
 * - `tasks.view_all` → everyone ('all' sentinel — no filtering).
 * - `tasks.view_team` → self + members of teams this user leads + direct
 *   reports (team_members.reportsTo === userId).
 * - neither → self only.
 *
 * `teams` is accepted for signature completeness (mirrors the page's data
 * shape) but is not consulted directly — the page derives visibility from
 * teamAssignments alone, same here.
 */
export function visibleMemberIds(input: VisibleMemberIdsInput): Set<string> | 'all' {
  const { userId, permissions, members, teamAssignments } = input;

  if (hasPermission(permissions, 'tasks.view_all')) {
    return 'all';
  }

  const ids = new Set<string>([userId]);

  if (hasPermission(permissions, 'tasks.view_team')) {
    const myTeamIds = teamAssignments
      .filter((ta) => ta.teamMemberId === userId && ta.teamRole === 'leader')
      .map((ta) => ta.teamId);
    const teamMemberIds = teamAssignments
      .filter((ta) => myTeamIds.includes(ta.teamId))
      .map((ta) => ta.teamMemberId);
    teamMemberIds.forEach((id) => ids.add(id));
    members.filter((m) => m.reportsTo === userId).forEach((m) => ids.add(m.id));
  }

  return ids;
}

/**
 * A task is visible if the current user created it, or any of its
 * assignments' teamMemberId is in the visible set (or the set is 'all').
 */
export function isTaskVisible(
  task: Task,
  assignments: TaskAssignment[],
  userId: string,
  visible: Set<string> | 'all'
): boolean {
  if (visible === 'all') return true;
  if (task.createdBy === userId) return true;
  const taskAssigns = assignments.filter((a) => a.taskId === task.id);
  return taskAssigns.some((a) => visible.has(a.teamMemberId));
}

/**
 * A task belongs to a project when a task_project_links row ties it to
 * that project — the ONLY project truth (Decision 20). The deprecated
 * `task.projectId` column is never read here, even when the server has
 * not yet nulled a legacy value on a given row.
 */
export function taskBelongsToProject(
  task: Task,
  links: TaskProjectLink[],
  projectId: string
): boolean {
  return links.some((lnk) => lnk.taskId === task.id && lnk.projectId === projectId);
}

/** Edit/complete gate: tasks.edit_own or tasks.edit_any. */
export function canEditTasks(permissions: string[] | undefined): boolean {
  return (
    hasPermission(permissions, 'tasks.edit_own') || hasPermission(permissions, 'tasks.edit_any')
  );
}

// ─── New: Tasks Kit permission + visibility helpers ───
// Given (perms, me, orgMembers) and a task + its assignments.

/** Page/widget access: any of the three view keys. */
export function canSeeTasks(permissions: string[] | undefined): boolean {
  return hasAnyPermission(permissions, ['tasks.view_own', 'tasks.view_team', 'tasks.view_all']);
}

/** I created it, I am assigned to it, or — for a subtask — either is true
 *  of its parent task (a subtask inherits "mine" from its parent). */
export function isMyTask(
  me: string,
  task: Task,
  assignments: TaskAssignment[],
  tasks: Task[]
): boolean {
  if (task.createdBy === me) return true;
  if (assignments.some((a) => a.taskId === task.id && a.teamMemberId === me)) return true;
  if (task.parentTaskId) {
    const parent = tasks.find((t) => t.id === task.parentTaskId);
    if (parent) return isMyTask(me, parent, assignments, tasks);
  }
  return false;
}

/**
 * Per-task visibility for the Tasks Kit:
 * - `tasks.view_all` → everyone.
 * - `tasks.view_team` → mine ∪ tasks created by/assigned to people BELOW
 *   me on the org chart (isBelowOnOrgChart) — replaces the old
 *   "led-team members + direct reports" approximation above.
 * - `tasks.view_own` (or neither key) → mine only.
 */
export function canSeeTask(
  permissions: string[] | undefined,
  me: string,
  orgMembers: OrgChartMember[],
  task: Task,
  assignments: TaskAssignment[],
  tasks: Task[]
): boolean {
  if (hasPermission(permissions, 'tasks.view_all')) return true;
  if (hasPermission(permissions, 'tasks.view_team')) {
    if (isMyTask(me, task, assignments, tasks)) return true;
    const taskAssigneeIds = assignments
      .filter((a) => a.taskId === task.id)
      .map((a) => a.teamMemberId);
    return [task.createdBy, ...taskAssigneeIds].some((id) => isBelowOnOrgChart(orgMembers, id, me));
  }
  return isMyTask(me, task, assignments, tasks);
}

export function canCreateTask(permissions: string[] | undefined): boolean {
  return hasPermission(permissions, 'tasks.create');
}

/** tasks.edit_any on any visible task, or tasks.edit_own on my task. */
export function canEditTask(
  permissions: string[] | undefined,
  me: string,
  orgMembers: OrgChartMember[],
  task: Task,
  assignments: TaskAssignment[],
  tasks: Task[]
): boolean {
  if (
    hasPermission(permissions, 'tasks.edit_any') &&
    canSeeTask(permissions, me, orgMembers, task, assignments, tasks)
  ) {
    return true;
  }
  if (hasPermission(permissions, 'tasks.edit_own') && isMyTask(me, task, assignments, tasks)) {
    return true;
  }
  return false;
}

export function canDeleteTask(permissions: string[] | undefined): boolean {
  return hasPermission(permissions, 'tasks.delete');
}

export function canArchiveTask(permissions: string[] | undefined): boolean {
  return hasPermission(permissions, 'tasks.archive');
}

export function canExportTasks(permissions: string[] | undefined): boolean {
  return hasPermission(permissions, 'tasks.export');
}

export function canComment(permissions: string[] | undefined): boolean {
  return hasPermission(permissions, 'tasks.create');
}

/** Whether the viewer can choose WHO gets assigned at all — false means the
 *  assignee picker is locked to "just me" (assignableMembers still returns
 *  `[me]` in that case so the field has something to show, but it should
 *  render read-only rather than as an editable multi-select). */
export function canPickAssignees(permissions: string[] | undefined): boolean {
  return hasAnyPermission(permissions, ['tasks.assign_team', 'tasks.assign_anyone']);
}

/** assign_anyone → everyone; assign_team → people below me on the org
 *  chart; neither → just me. Mirrors the server's own enforcement via
 *  shared/orgChart.ts's assignableMembers. */
export function assignableMembers<T extends OrgChartMember>(
  permissions: string[] | undefined,
  me: string,
  members: T[]
): T[] {
  const assignAnyone = hasPermission(permissions, 'tasks.assign_anyone');
  const assignTeam = hasPermission(permissions, 'tasks.assign_team');
  if (!assignAnyone && !assignTeam) {
    return members.filter((m) => m.id === me);
  }
  return orgAssignableMembers(members, me, { assignAnyone, assignTeam });
}
